// ─────────────────────────────────────────────────────────────────────────────
// «Зураггүй бараа» — дэлгүүрийн ажилтан утсаараа зураг авч ТҮР (draft) болгоно.
//
// Урсгал: зураггүй барааны жагсаалт → мөр бүрт «Зураг авах» → утсанд камер шууд
// нээгдэнэ (<input capture="environment">) → backend-д draft болгож илгээнэ →
// мөрөнд draft badge + thumbnail гарна. Дахин авч болно.
//
// ⚠️ Энэ зураг АППАД ХАРАГДАХГҮЙ (backend drafts/ — image_assets-д орохгүй). Эзэн
//    дараа нь татаж аваад чанар сайжруулж каталогт оруулна.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  fetchImagelessProducts,
  uploadDraftImage,
  lookupDraftProduct,
  removeDraftProduct,
} from "../../api/axios/requests";
import ScrollToTopButton from "../../components/scrolltotop";
import EmptyStateComponent from "../../components/empty";
import BarcodeScanner from "../../components/barcode-scanner";
import ConfirmPasswordModal from "../../modals/confirm-password";
import { useScrollToTop } from "../../hooks/userScrollToTop";

// «⋮» цэсний товч (caret-гүй, муут) — устгахыг нуухын тулд.
const KebabToggle = React.forwardRef(function KebabToggle({ onClick, disabled }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className="btn btn-link text-muted p-0 d-flex align-items-center justify-content-center flex-shrink-0 shadow-none"
      style={{ width: 36, height: 44 }}
      disabled={disabled}
      aria-label="Бусад"
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      <i className="uil uil-ellipsis-v" style={{ fontSize: 20 }} />
    </button>
  );
});

// Утас/таблет (хуруугаар) → камер; desktop (хулгана) → файл оруулах.
const IS_TOUCH =
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(pointer: coarse)").matches
    : false;

// Зургийг хөтөч дээр шахна (resize + JPEG) — nginx-ийн ~1MB хязгаарт багтааж,
// LTE дээр хурдан илгээхийн тулд. Draft-д чанар хангалттай (эзэн дараа сайжруулна).
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  try {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    }
    let w = bitmap.width;
    let h = bitmap.height;
    if (!w || !h) return file;
    if (Math.max(w, h) > maxDim) {
      const s = maxDim / Math.max(w, h);
      w = Math.round(w * s);
      h = Math.round(h * s);
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // Хэрэв шахсан нь эх файлаас том бол (жижиг зураг) эхийг нь үлдээнэ.
    if (blob.size >= file.size && file.size < 900 * 1024) return file;
    return new File([blob], "photo.jpg", { type: "image/jpeg" });
  } catch (e) {
    console.error("Зураг шахах алдаа:", e);
    return file; // шахаж чадсангүй — эх файлаар оролдоно
  }
}

export default function CaptureImagesScreen() {
  const { showScrollButton, scrollToTop } = useScrollToTop();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [search, setSearch] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [scanning, setScanning] = useState(false);
  // Нууц үгээр баталгаажуулж аппаас хасах бараа.
  const [pendingRemove, setPendingRemove] = useState(null);

  // Нэг нуусан input — товч дармагц ямар бараанд зориулж байгааг ref-д тэмдэглэнэ.
  const fileInputRef = useRef(null);
  const targetProductRef = useRef(null);

  const loadProducts = async (pageToLoad = 1, isRefresh = false) => {
    try {
      setLoading(true);
      const res = await fetchImagelessProducts({
        page: pageToLoad,
        keyword: search,
      });
      const chunk = Array.isArray(res?.data) ? res.data : [];
      setItems((prev) => (isRefresh ? chunk : [...prev, ...chunk]));
      const pg = res?.pagination || {};
      setTotal(pg.totalItems || 0);
      setRemaining(pg.remaining ?? pg.totalItems ?? 0);
      setHasMore((pg.currentPage || pageToLoad) < (pg.totalPages || 0));
      setPage(pg.currentPage || pageToLoad);
    } catch (error) {
      console.error("Зураггүй бараа татах алдаа:", error);
      toast.error("Жагсаалт татахад алдаа гарлаа.");
    } finally {
      setLoading(false);
    }
  };

  // Хайлт өөрчлөгдвөл дахин татна (400мс debounce). Эхний ачаалалт ч энд.
  useEffect(() => {
    const t = setTimeout(() => loadProducts(1, true), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleLoadMore = () => {
    if (hasMore && !loading) loadProducts(page + 1, false);
  };

  // Нуусан зургийн input-ийг тухайн бараанд зориулж нээнэ.
  const openPhotoFor = (product) => {
    targetProductRef.current = product;
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // ижил файлыг дахин сонгож болохын тулд
      fileInputRef.current.click();
    }
  };

  // «Зураг авах» → тухайн барааны камер нээнэ.
  const triggerCapture = (product) => {
    if (uploadingId) return;
    openPhotoFor(product);
  };

  // «Хасах» → нууц үгийн баталгаажуулалт нээнэ.
  const requestRemove = (product) => {
    if (uploadingId) return;
    setPendingRemove(product);
  };

  // Нууц үг зөв бол → барааг аппаас нуух (isVisible=0, буцаах боломжтой).
  const handleRemoveConfirmed = async () => {
    const product = pendingRemove;
    if (!product) return;
    try {
      await removeDraftProduct(product.productId);
      setItems((prev) => prev.filter((p) => p.productId !== product.productId));
      setTotal((t) => Math.max(0, t - 1));
      setRemaining((r) => (product.hasDraft ? r : Math.max(0, r - 1)));
      toast.success(
        product.name ? `Хасагдлаа: ${product.name}` : "Бараа хасагдлаа."
      );
    } catch (error) {
      console.error("Хасах алдаа:", error);
      toast.error("Хасахад алдаа гарлаа.");
    } finally {
      setPendingRemove(null);
    }
  };

  // Баркод уншигдвал: дэлгүүрийн барааг олоод ШУУД зургийн камер нээнэ.
  const handleScanDetected = useCallback(async (barcode) => {
    setScanning(false);
    try {
      const res = await lookupDraftProduct(barcode);
      if (!res?.success || !res?.data?.found) {
        toast.error(`Бараа олдсонгүй: ${barcode}`);
        return;
      }
      const d = res.data;
      openPhotoFor({ productId: d.productId, name: d.name, barcode: d.barcode });
    } catch (error) {
      console.error("Баркод хайх алдаа:", error);
      toast.error("Бараа хайхад алдаа гарлаа.");
    }
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    const product = targetProductRef.current;
    if (!file || !product) return;

    try {
      setUploadingId(product.productId);
      const toUpload = await compressImage(file);
      const res = await uploadDraftImage(toUpload, product.productId);
      if (!res?.success) throw new Error(res?.message || "Алдаа");
      const url = res?.data?.url || null;
      setItems((prev) =>
        prev.map((p) =>
          p.productId === product.productId ? { ...p, hasDraft: true, draftUrl: url } : p
        )
      );
      toast.success(product.name ? `Зураг хадгалагдлаа: ${product.name}` : "Зураг хадгалагдлаа.");
    } catch (error) {
      console.error("Draft upload алдаа:", error);
      const msg = error?.response?.data?.message || error?.message || "Зураг оруулахад алдаа гарлаа.";
      toast.error(msg);
    } finally {
      setUploadingId(null);
      targetProductRef.current = null;
    }
  };

  const isEmpty = !loading && items.length === 0;

  return (
    <div className="container-fluid app">
      <div className="row">
        <div className="col-12">
          <h4 className="page-title mt-3">Зураггүй бараа</h4>

          {/* Баркод товч + нэр хайлт — desktop зэрэгцээ, утсанд дээш доош */}
          <div
            className="d-flex flex-column flex-sm-row gap-2 mb-2"
            style={{ maxWidth: 640 }}
          >
            <button
              type="button"
              className="btn btn-danger flex-shrink-0"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setScanning(true)}
            >
              <i className="uil uil-focus" /> Баркод уншуулах
            </button>
            <input
              type="search"
              className="form-control flex-grow-1"
              placeholder="Нэрээр хайх"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {/* Зураг авах үлдсэн тоо */}
          <div className="text-muted font-13 mb-3">
            Зураг авах үлдсэн: <strong>{remaining}</strong>
            {total ? ` / ${total}` : ""}
          </div>
        </div>
      </div>

      {/* Нуусан камер/файл сонгогч — утсанд ард талын камер нээгдэнэ */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        {...(IS_TOUCH ? { capture: "environment" } : {})}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <div className="row">
        <div className="col-12">
          {isEmpty ? (
            <EmptyStateComponent
              title={"Зураггүй бараа алга"}
              subtitle={"Бүх бараа зурагтай эсвэл хайлтад тохирох бараа олдсонгүй"}
            />
          ) : (
            <div className="row g-2">
              {items.map((p) => (
                <div className="col-12 col-sm-6 col-lg-4" key={p.productId}>
                  <div className="card mb-0 h-100">
                    <div className="card-body d-flex align-items-center gap-2 p-2">
                      <div
                        className="flex-shrink-0 d-flex align-items-center justify-content-center rounded bg-light"
                        style={{ width: 64, height: 64, overflow: "hidden" }}
                      >
                        {p.hasDraft && p.draftUrl ? (
                          <img
                            src={p.draftUrl}
                            alt={p.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <i className="uil uil-image text-muted" style={{ fontSize: 28 }} />
                        )}
                      </div>

                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="fw-bold text-truncate" title={p.name}>
                          {p.name}
                        </div>
                        <div className="text-muted font-12 text-truncate">{p.barcode}</div>
                        {p.hasDraft && (
                          <span className="badge bg-success-subtle text-success mt-1">
                            <i className="uil uil-check" /> Зураг авсан
                          </span>
                        )}
                      </div>

                      <div className="d-flex align-items-center gap-1 flex-shrink-0">
                        {/* Зураг авах / оруулах — үндсэн үйлдэл */}
                        <button
                          type="button"
                          className={`btn rounded-circle d-flex align-items-center justify-content-center ${
                            p.hasDraft ? "btn-outline-success" : "btn-danger"
                          }`}
                          style={{ width: 44, height: 44, padding: 0 }}
                          disabled={uploadingId === p.productId}
                          onClick={() => triggerCapture(p)}
                          title={p.hasDraft ? "Дахин зураг" : IS_TOUCH ? "Зураг авах" : "Зураг оруулах"}
                          aria-label={p.hasDraft ? "Дахин зураг" : IS_TOUCH ? "Зураг авах" : "Зураг оруулах"}
                        >
                          {uploadingId === p.productId ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                          ) : (
                            <i
                              className={`uil ${IS_TOUCH ? "uil-camera" : "uil-upload"}`}
                              style={{ fontSize: 20 }}
                            />
                          )}
                        </button>

                        {/* Бусад (⋮) — устгахыг нуув, санамсаргүй дарахаас сэргийлнэ */}
                        <Dropdown align="end">
                          <Dropdown.Toggle
                            as={KebabToggle}
                            disabled={uploadingId === p.productId}
                          />
                          <Dropdown.Menu>
                            <Dropdown.Item
                              className="text-danger"
                              onClick={() => requestRemove(p)}
                            >
                              <i className="uil uil-trash-alt me-1" /> Аппаас хасах
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasMore && (
            <div className="text-center my-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? "Ачааллаж байна..." : "Цааш үзэх"}
              </button>
            </div>
          )}
          {loading && !items.length && (
            <div className="text-center my-4 text-muted">Ачааллаж байна...</div>
          )}
        </div>
      </div>

      {scanning && (
        <BarcodeScanner
          onDetected={handleScanDetected}
          onClose={() => setScanning(false)}
        />
      )}

      {/* Аппаас хасахаас өмнө нууц үг баталгаажуулах */}
      <ConfirmPasswordModal
        show={!!pendingRemove}
        handleClose={() => setPendingRemove(null)}
        onSuccess={handleRemoveConfirmed}
      />

      {showScrollButton && <ScrollToTopButton onClick={scrollToTop} />}
    </div>
  );
}
